import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Mail, Trash2, Plus, Star, PenLine } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EmailSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '', is_default: false });

  const [smtp, setSmtp] = useState({
    smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '',
    smtp_use_tls: true, smtp_use_ssl: false, smtp_from_email: '', smtp_from_name: 'Expertfix',
  });

  const fetchSettings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/settings`);
      const s = r.data;
      setSettings(s);
      setSmtp({
        smtp_host: s.smtp_host || '', smtp_port: s.smtp_port || 587,
        smtp_username: s.smtp_username || '', smtp_password: s.smtp_password || '',
        smtp_use_tls: s.smtp_use_tls ?? true, smtp_use_ssl: s.smtp_use_ssl ?? false,
        smtp_from_email: s.smtp_from_email || '', smtp_from_name: s.smtp_from_name || 'Expertfix',
      });
    } catch {}
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/email-templates`);
      setTemplates(r.data);
    } catch {}
  }, []);

  useEffect(() => { fetchSettings(); fetchTemplates(); }, [fetchSettings, fetchTemplates]);

  const saveSmtp = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings`, smtp);
      toast.success(t('invoiceExtra.smtpSettingsSaved'));
      fetchSettings();
    } catch { toast.error(t('invoiceExtra.failedToSave')); }
    finally { setSaving(false); }
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject) { toast.error(t('invoiceExtra.nameAndSubjectRequired')); return; }
    try {
      if (editTemplate) {
        await axios.put(`${API}/api/email-templates/${editTemplate.id}`, templateForm);
        toast.success(t('invoiceExtra.templateUpdated'));
      } else {
        await axios.post(`${API}/api/email-templates`, templateForm);
        toast.success(t('invoiceExtra.templateCreated'));
      }
      setShowTemplateDialog(false);
      setEditTemplate(null);
      fetchTemplates();
    } catch { toast.error(t('invoiceExtra.failedToSaveTemplate')); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm(t('invoiceExtra.deleteThisTemplate'))) return;
    try {
      await axios.delete(`${API}/api/email-templates/${id}`);
      toast.success(t('invoiceExtra.deleted'));
      fetchTemplates();
    } catch { toast.error(t('invoiceExtra.failed')); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-[#AA0000]" /> {t('invoiceExtra.smtpConfiguration')}</CardTitle>
          <CardDescription>{t('invoiceExtra.configureEmailServerForSendingInvoices')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t('invoiceExtra.smtpHost')}</Label><Input value={smtp.smtp_host} onChange={e => setSmtp(p => ({...p, smtp_host: e.target.value}))} placeholder="smtp.gmail.com" data-testid="smtp-host" /></div>
            <div><Label>{t('invoiceExtra.smtpPort')}</Label><Input type="number" value={smtp.smtp_port} onChange={e => setSmtp(p => ({...p, smtp_port: parseInt(e.target.value) || 587}))} data-testid="smtp-port" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t('invoiceExtra.username')}</Label><Input value={smtp.smtp_username} onChange={e => setSmtp(p => ({...p, smtp_username: e.target.value}))} placeholder="user@example.com" data-testid="smtp-username" /></div>
            <div><Label>{t('invoiceExtra.password')}</Label><Input type="password" value={smtp.smtp_password} onChange={e => setSmtp(p => ({...p, smtp_password: e.target.value}))} placeholder="••••••" data-testid="smtp-password" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>{t('invoiceExtra.fromEmail')}</Label><Input value={smtp.smtp_from_email} onChange={e => setSmtp(p => ({...p, smtp_from_email: e.target.value}))} placeholder="invoices@company.com" data-testid="smtp-from-email" /></div>
            <div><Label>{t('invoiceExtra.fromName')}</Label><Input value={smtp.smtp_from_name} onChange={e => setSmtp(p => ({...p, smtp_from_name: e.target.value}))} data-testid="smtp-from-name" /></div>
          </div>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox id="use_tls" checked={smtp.smtp_use_tls} onCheckedChange={v => setSmtp(p => ({...p, smtp_use_tls: v, smtp_use_ssl: v ? false : p.smtp_use_ssl}))} />
              <Label htmlFor="use_tls">{t('invoiceExtra.useTlsStarttls')}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="use_ssl" checked={smtp.smtp_use_ssl} onCheckedChange={v => setSmtp(p => ({...p, smtp_use_ssl: v, smtp_use_tls: v ? false : p.smtp_use_tls}))} />
              <Label htmlFor="use_ssl">{t('invoiceExtra.useSsl')}</Label>
            </div>
          </div>
          <Button onClick={saveSmtp} disabled={saving} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="save-smtp-btn">
            {saving ? t('invoiceExtra.saving') : t('invoiceExtra.saveSmtpSettings')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>{t('invoiceExtra.emailTemplates')}</CardTitle>
              <CardDescription>Reusable templates for invoice emails. Use {'{invoice_number}'} as placeholder.</CardDescription>
            </div>
            <Button size="sm" onClick={() => { setEditTemplate(null); setTemplateForm({ name: '', subject: '', body: '', is_default: false }); setShowTemplateDialog(true); }}
              className="bg-[#AA0000] hover:bg-[#880000]" data-testid="add-template-btn">
              <Plus className="h-4 w-4 mr-1" /> {t('invoiceExtra.addTemplate')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-6 text-slate-400">{t('invoiceExtra.noEmailTemplatesYet')}</div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t('invoiceExtra.name')}</TableHead><TableHead>{t('invoiceExtra.subject')}</TableHead><TableHead className="text-center">{t('invoiceExtra.default')}</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {templates.map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell className="max-w-[250px] truncate">{t.subject}</TableCell>
                    <TableCell className="text-center">{t.is_default && <Star className="h-4 w-4 text-yellow-500 mx-auto" />}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => {
                          setEditTemplate(t);
                          setTemplateForm({ name: t.name, subject: t.subject, body: t.body, is_default: t.is_default });
                          setShowTemplateDialog(true);
                        }}><PenLine className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteTemplate(t.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader><DialogTitle>{editTemplate ? t('invoiceExtra.editTemplate') : t('invoiceExtra.newEmailTemplate')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('invoiceExtra.templateName')}</Label><Input value={templateForm.name} onChange={e => setTemplateForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Standard Invoice" data-testid="template-name" /></div>
            <div><Label>{t('invoiceExtra.subject')}</Label><Input value={templateForm.subject} onChange={e => setTemplateForm(p => ({...p, subject: e.target.value}))} placeholder="Invoice {invoice_number}" data-testid="template-subject" /></div>
            <div><Label>{t('invoiceExtra.bodyHtml')}</Label><Textarea value={templateForm.body} onChange={e => setTemplateForm(p => ({...p, body: e.target.value}))} rows={8}
              placeholder="<p>Dear Customer,</p><p>Please find attached invoice {invoice_number}.</p>" data-testid="template-body" /></div>
            <div className="flex items-center space-x-2">
              <Checkbox id="tmpl_default" checked={templateForm.is_default} onCheckedChange={v => setTemplateForm(p => ({...p, is_default: v}))} />
              <Label htmlFor="tmpl_default">{t('invoiceExtra.setAsDefaultTemplate')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)} data-testid="dialog-cancel-btn">{t('invoiceExtra.cancel')}</Button>
            <Button onClick={saveTemplate} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="save-template-btn">{t('invoiceExtra.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailSettings;
